import { CapitalActivityMetadata } from "gen/model/capitalActivityMetadata";
import { CapitalActivityViewTypeEnum } from "gen/model/capitalActivityViewTypeEnum";
import { CeleryWorkflowStateMachineFieldsEnum } from "gen/model/celeryWorkflowStateMachineFieldsEnum";
import { GenericTableCell } from "gen/model/genericTableCell";
import { GenericTableData } from "gen/model/genericTableData";
import { MoneyTableCellValue } from "gen/model/moneyTableCellValue";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { TextTableCellValue } from "gen/model/textTableCellValue";
import { useCallback } from "react";
import {
  GetActionsForCellFn,
  GetLinkInfoForCellFn,
  GetRowSpanKeyForCellFn,
  RenderCellValueProps,
} from "shared/components/table/data/DataTableTypes";
import { rootRoute } from "shared/navigation/Routes";
import { useHasPermission } from "shared/utils/PermissionUtils";

export interface UseCapitalActivityTableRendererProps {
  data: GenericTableData;
  fundFamilyId: string;
  fundFamilyEventId: string;
  isCellLoading?: (props: RenderCellValueProps) => boolean;
  isReview?: boolean;
}

export const useCapitalActivityTableProps = ({
  data,
  fundFamilyId,
  fundFamilyEventId,
  isCellLoading,
  isReview,
}: UseCapitalActivityTableRendererProps) => {
  const isWriteActive = useHasPermission(PermissionTypeEnum.write);

  const getRowSpanKeyForCell = useCallback<GetRowSpanKeyForCellFn>(
    (props) => {
      const { cell } = props;
      if (isEqualizationCell(cell)) {
        return `equalization_${cell.metadata.fund_family_id}_${cell.metadata.fund_family_sub_event_id}`;
      }
      const buttonCellCheck =
        isButtonCell(props, isCellLoading) && !isWaterfallCell(cell);
      if (buttonCellCheck) {
        return `create_${cell.metadata?.type}`;
      }
      return undefined;
    },
    [isCellLoading],
  );

  const getLinkInfoForCell = useCallback<GetLinkInfoForCellFn>(
    (props) => {
      if (props.type === "header") {
        const hasEditableCell =
          isReview ||
          data.rows.some(
            (row) =>
              isEditableCell(row.cells[props.columnContext.column.id]) &&
              row.cells[props.columnContext.column.id].value?.type === "money",
          );
        if (
          props.columnContext.column.id === "capital_call" &&
          hasEditableCell
        ) {
          return {
            type: "generic",
            variant: "tableCellAction",
            to: rootRoute({})
              .fundFamilies({})
              .fundFamily({ fundFamilyId })
              .capitalActivity({})
              .capitalActivityEvent({ fundFamilyEventId })
              .calls({})
              .capitalActivityCapitalCallEventOverview({}).$,
          };
        }
        if (
          props.columnContext.column.id === "distribution" &&
          hasEditableCell
        ) {
          const distributionRoute = rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .capitalActivity({})
            .capitalActivityEvent({ fundFamilyEventId })
            .distributions({});
          return {
            type: "generic",
            variant: "tableCellAction",
            to: distributionRoute
              .capitalActivityEventDistributions({})
              .capitalActivityEventDistributionOverview({}).$,
          };
        }
      }
      if (props.type !== "data") {
        return undefined;
      }
      const { cell } = props;
      if (isButtonCell(props, isCellLoading)) {
        const isDisabled =
          !isWriteActive ||
          (cell.metadata?.type === "capital_activity" &&
            cell.metadata.is_button &&
            cell.metadata.disabled);
        return {
          type: "button",
          colorScheme: isEqualizationCell(cell) ? "tertiary" : undefined,
          isDisabled,
          isLoading: isCellLoading?.(props),
        };
      } else if (isExistingWaterfallCell(cell)) {
        const { waterfall_is_hybrid, view_type } =
          cell.metadata as CapitalActivityMetadata;
        const destination =
          waterfall_is_hybrid &&
          view_type === CapitalActivityViewTypeEnum.editable
            ? rootRoute({})
                .fundFamilies({})
                .fundFamily({ fundFamilyId })
                .capitalActivity({})
                .capitalActivityEvent({ fundFamilyEventId })
                .waterfall({})
                .hybrid({
                  fundFamilySubEventId: cell.metadata.fund_family_sub_event_id!,
                  fundEntityId: cell.metadata.fund_id!,
                })
                .status({}).$
            : rootRoute({})
                .fundFamilies({})
                .fundFamily({ fundFamilyId })
                .capitalActivity({})
                .capitalActivityEvent({ fundFamilyEventId })
                .waterfall({})
                .review({
                  fundFamilySubEventId: cell.metadata.fund_family_sub_event_id!,
                }).$;
        return {
          type: "generic",
          to: destination,
          variant: "tableCellAction",
          isLoading: isCellLoading?.(props),
        };
      } else if (isEqualizationCell(cell)) {
        return {
          type: "generic",
          variant: "tableCellAction",
          to: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .capitalActivity({})
            .capitalActivityEvent({ fundFamilyEventId })
            .equalization({})
            .view({
              fundFamilySubEventId: cell.metadata.fund_family_sub_event_id!,
            }).$,
          isLoading: isCellLoading?.(props),
        };
      }
    },
    [
      fundFamilyEventId,
      fundFamilyId,
      isCellLoading,
      isReview,
      isWriteActive,
      data.rows,
    ],
  );

  const getActionsForCell = useCallback<GetActionsForCellFn>(
    ({ cell }) => {
      if (isEditableCell(cell) && cell.metadata.cell_type === "waterfall") {
        return {
          showEdit: isWriteActive && cell.value.type === "money",
          showDelete: isWriteActive,
        };
      }
    },
    [isWriteActive],
  );

  return {
    getLinkInfoForCell,
    getRowSpanKeyForCell,
    getActionsForCell,
  };
};

const isEqualizationCell = (
  cell?: GenericTableCell,
): cell is GenericTableCell & {
  value: TextTableCellValue;
  metadata: CapitalActivityMetadata;
} =>
  Boolean(
    cell?.metadata?.type === "capital_activity" &&
      cell.metadata.cell_type === "equalization",
  );

const isWaterfallCell = (
  cell?: GenericTableCell,
): cell is GenericTableCell & {
  value: TextTableCellValue;
  metadata: CapitalActivityMetadata;
} =>
  Boolean(
    cell?.value?.type === "text" &&
      cell.metadata?.type === "capital_activity" &&
      cell.metadata.cell_type === "waterfall",
  );

const isExistingWaterfallCell = (
  cell?: GenericTableCell,
): cell is GenericTableCell & {
  value: TextTableCellValue;
  metadata: CapitalActivityMetadata;
} =>
  Boolean(
    cell?.value?.type === "text" &&
      cell.metadata?.type === "capital_activity" &&
      cell.metadata.cell_type === "waterfall" &&
      cell.metadata.waterfall_run_id,
  );

const isOutOfDateWaterfallCell = (
  props: RenderCellValueProps,
): props is RenderCellValueProps & {
  cell: GenericTableCell & {
    metadata: CapitalActivityMetadata;
  };
} =>
  Boolean(
    props.cell.metadata?.type === "capital_activity" &&
      props.cell.metadata.cell_type === "waterfall" &&
      props.cell.metadata.out_of_date,
  );

const isEditableCell = (
  cell?: GenericTableCell,
): cell is GenericTableCell & {
  value: TextTableCellValue | MoneyTableCellValue;
  metadata: CapitalActivityMetadata;
} =>
  cell?.metadata?.type === "capital_activity" &&
  cell.metadata.view_type === CapitalActivityViewTypeEnum.editable &&
  // Force not editable if celery is still working
  (!cell.metadata.celery_state ||
    cell.metadata.celery_state ===
      CeleryWorkflowStateMachineFieldsEnum.complete ||
    cell.metadata.celery_state ===
      CeleryWorkflowStateMachineFieldsEnum.failed) &&
  (cell.value?.type === "money" || cell.value?.type === "text");

const isButtonCell = (
  props: RenderCellValueProps,
  isCellLoading: (props: RenderCellValueProps) => boolean = () => false,
): props is RenderCellValueProps & {
  cell: GenericTableCell & {
    value: MoneyTableCellValue | TextTableCellValue;
  };
} =>
  Boolean(
    // Force loading state to be a button with spinner
    isCellLoading(props) ||
      (props.cell.metadata?.type === "capital_activity" &&
        props.cell.metadata.is_button &&
        !isOutOfDateWaterfallCell(props)),
  );
